import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { formatUserName } from '@app/helpers/formatName.helper';
import type { UserAccount } from '@app/models/account.model';
import { GetLocalizedPipe } from '@app/pipes/get-localized.pipe';
import { UserService } from '@app/services/admin/user.service';
import { ToastService } from '@app/services/toast.service';
import { GenericStore } from '@app/stores/generic.store';
import { GridTableComponent } from '@app/ui-components/grid-table/grid-table.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import type { ColDef, IDatasource, IGetRowsParams } from 'ag-grid-community';
import { finalize } from 'rxjs';

type UserRow = UserAccount & {
  name: string;
  email: string;
  country: string;
};

@Component({
  selector: 'app-user-list',
  standalone: true,
  imports: [CommonModule, GridTableComponent, TranslatePipe],
  templateUrl: './user-list.component.html',
  styleUrl: './user-list.component.scss',
  providers: [GetLocalizedPipe],
})
export class UserListComponent implements OnInit {
  private readonly userService = inject(UserService);
  private readonly translate = inject(TranslateService);
  private readonly toast = inject(ToastService);
  private readonly store = inject(GenericStore);
  private readonly localizedPipe = inject(GetLocalizedPipe);

  readonly isLoading = signal(true);
  readonly searchValue = signal<string | undefined>(undefined);

  columns: ColDef<UserRow>[] = [];

  readonly usersDatasource: IDatasource = {
    getRows: (params) => this.loadUsers(params),
  };

  private buildColumns(): void {
    this.columns = [
      {
        field: 'name',
        headerName: this.translate.instant('admin.users.table.name'),
      },
      {
        field: 'email',
        headerName: this.translate.instant('admin.users.table.email'),
      },
      {
        field: 'country',
        headerName: this.translate.instant('admin.users.table.country'),
      },
    ];
  }

  ngOnInit(): void {
    this.buildColumns();
    this.translate.onLangChange.subscribe(() => this.buildColumns());
  }

  private loadUsers(params: IGetRowsParams): void {
    this.isLoading.set(true);

    const skip = params.startRow;
    const take = params.endRow - params.startRow;

    const sortModel = params.sortModel ?? [];
    const sort = sortModel.map((s) => ({
      selector: s.colId,
      desc: s.sort === 'desc',
    }));

    const filter = this.searchValue() ?? '';

    this.userService
      .getUsers({ skip, take, sort, filter })
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (res) => {
          const list: UserAccount[] = res?.data ?? [];
          const active = list.filter((u) => !u.deleted);

          const rows: UserRow[] = active.map((user) => {
            const name = formatUserName(user);
            const code = user.personalData?.address?.countryCode ?? null;

            const entry = this.store
              .countriesEntities()
              .find((c) => c.code === code);

            const countryLabel =
              this.localizedPipe.transform(entry?.localized ?? null) ?? '—';

            return {
              ...user,
              name,
              email: user.email ?? '—',
              country: countryLabel,
            };
          });

          const rowCount = res.rowCount ?? rows.length;
          params.successCallback(rows, rowCount);
        },
        error: () => {
          params.failCallback();
          this.toast.error(
            this.translate.instant('admin.toasts.tableLoadFailed')
          );
        },
      });
  }

  onSearchChange(term: string | undefined): void {
    this.searchValue.set(term || undefined);
  }
}
